'use client';

import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { usePostHog } from '@posthog/react';

import type { Entry, Profile } from '@hub/common/src/types/cit-public-api';

import { queryFunctions } from '@hub/common/src/query-functions';
import {
  getEntryLink,
  getProfileInstanceLink,
  toUrlSafeSlug
} from '@hub/common/src/navigationUtils';

import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';
import { capturePageview } from './PostHogAnalyticsProvider';

import ProfileDetailLayout from './ProfileDetailLayout';
import EntryDetailLayout from './EntryDetailLayout';
import CloseIcon from './SVG/CloseIcon';

export interface ReferenceType {
  referenceLink: string;
  referenceType: 'profile' | 'entry';
  profileTypeURI?: string;
}

export function ReferenceOverlay({
  selectedReference,
  setSelectedReference,
  accountId
}: {
  selectedReference: ReferenceType | undefined;
  setSelectedReference: Dispatch<SetStateAction<ReferenceType | undefined>>;
  accountId: string;
}) {
  const citOrigin = useCitOriginClient();

  const { referenceLink, referenceType, profileTypeURI } = selectedReference || {};

  const encodedProfileTypeURI = profileTypeURI ? encodeURIComponent(profileTypeURI) : undefined;
  const encodedProfileURI =
    referenceType === 'profile' && referenceLink ? encodeURIComponent(referenceLink) : undefined;

  const posthog = usePostHog();

  const { data: profileData } = useQuery({
    queryKey: [
      'profile-info',
      {
        accountId,
        profileURI: encodedProfileURI || '',
        profileTypeURI: encodedProfileTypeURI || '',
        citOrigin
      }
    ],
    queryFn: queryFunctions.getProfileDetails,
    enabled: !!selectedReference && referenceType === 'profile'
  });

  const { data: entryData } = useQuery({
    queryKey: [
      'entry-details',
      { accountId, folderId: undefined, entryId: referenceLink || '', citOrigin }
    ],
    queryFn: queryFunctions.getEntryDetails,
    enabled: !!selectedReference && referenceType === 'entry'
  });

  const isOverlayOpen = !!selectedReference;

  // the same X, in the same top-right spot, as the modal's own close button; the overlay covers that one
  const closeButton = (
    <button
      type='button'
      aria-label='Close reference'
      onClick={() => setSelectedReference(undefined)}
    >
      <CloseIcon />
    </button>
  );

  const [overlayReference, setOverlayReference] = useState<Profile | Entry | undefined>();

  useEffect(() => {
    if (profileData) {
      // eslint-disable-next-line
      setOverlayReference(profileData);
    } else if (entryData) {
      setOverlayReference(entryData);
    }
  }, [profileData, entryData]);

  // the reference this overlay has reported a pageview for; cleared on close so reopening reports again
  const reportedReference = useRef<string | null>(null);

  useEffect(() => {
    // manually capture pageview events when overlay opens since url does not change. waits for the
    // reference to load, so a broken one is not counted and the url carries its slug, built with the same
    // helpers as the real routes so it matches the page the visitor would land on opening it directly
    if (!isOverlayOpen || !referenceLink) {
      reportedReference.current = null;
      return;
    }

    const loaded = referenceType === 'profile' ? profileData : entryData;
    if (!loaded || reportedReference.current === referenceLink) {
      return;
    }
    reportedReference.current = referenceLink;

    const pathname =
      referenceType === 'profile'
        ? getProfileInstanceLink(
            (loaded as Profile).type,
            toUrlSafeSlug(loaded.slug || loaded.id),
            citOrigin,
            accountId,
            true
          )
        : getEntryLink(loaded.slug || loaded.id, citOrigin, accountId);
    capturePageview(posthog, window.location.origin + pathname, {
      $pathname: pathname,
      $host: window.location.host
    });
  }, [
    isOverlayOpen,
    posthog,
    referenceLink,
    referenceType,
    profileData,
    entryData,
    citOrigin,
    accountId
  ]);

  return (
    <div
      aria-hidden={!isOverlayOpen}
      className={[
        'profile-overlay-container absolute inset-0 z-[1000] flex overflow-hidden',
        'transform-gpu transition-all duration-500',
        'ease-out data-[state=closed]:ease-in',
        'origin-center',
        isOverlayOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-50',
        isOverlayOpen ? 'pointer-events-auto' : 'pointer-events-none'
      ].join(' ')}
      data-state={isOverlayOpen ? 'open' : 'closed'}
    >
      <div className='profile-overlay-content flex-1 w-full z-[1000]'>
        {overlayReference && 'type' in overlayReference ? (
          <ProfileDetailLayout
            accountId={accountId}
            key={overlayReference?.id || profileData?.id}
            profile={!isOverlayOpen ? overlayReference : (profileData ?? null)}
            closeButton={closeButton}
            // enable gallery for overlay profile detail if the overlay is open
            enableGallery={isOverlayOpen}
            isOverlay={true}
          />
        ) : overlayReference && 'classification' in overlayReference ? (
          <EntryDetailLayout
            accountId={accountId}
            key={overlayReference?.id || entryData?.id}
            entry={!isOverlayOpen ? overlayReference : (entryData ?? null)}
            closeButton={closeButton}
            enableGallery={isOverlayOpen}
            isOverlay={true}
          />
        ) : null}
      </div>
    </div>
  );
}
